import { useState, type FormEvent } from 'react';
import { Alert, ApiError, Button, Field, Input } from '@busbook/ui';
import { useSession } from '../lib/session.js';

/**
 * Shown instead of the console when the account is on a password somebody else chose.
 *
 * There is no way past it and no sign-out-and-carry-on: the session can reach exactly one endpoint,
 * so every other screen would render into a wall of refused requests. Making it the whole screen is
 * the honest rendering of what the account can currently do.
 */
export function ChangePassword() {
  const { signOut, changePassword } = useSession();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      // Checked here rather than at the server: a mistyped confirmation is not a failed request,
      // and sending it would spend the temporary password on a password nobody knows.
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await changePassword(password);
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'VALIDATION_ERROR'
          ? 'That password is not strong enough. Try a longer one.'
          : err instanceof ApiError
            ? err.message
            : 'Could not set the password. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-screen place-items-center px-6">
      <div className="w-full max-w-sm">
        <h1 className="text-lg font-semibold">Choose a password</h1>
        <p className="mt-2 text-sm" style={{ color: 'var(--color-muted)' }}>
          You signed in with a temporary password. Pick one only you know before carrying on — it is
          the only thing this sign-in can be used for.
        </p>

        <form className="mt-6 flex flex-col gap-4" onSubmit={submit}>
          <Field label="New password">
            <Input
              type="password"
              autoComplete="new-password"
              autoFocus
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          <Field label="Confirm new password">
            <Input
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>

          {error && <Alert tone="error">{error}</Alert>}

          <Button type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Set password and continue'}
          </Button>

          <Button type="button" variant="ghost" onClick={signOut} disabled={busy}>
            Sign out
          </Button>
        </form>
      </div>
    </main>
  );
}
